<script lang="ts" setup>
  import { computed, useId, useSlots } from 'vue';
  import { useClipboard } from '@vueuse/core';
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { FontIcon } from '../FontIcon';
  import { Button, ButtonSize, ButtonVariant } from '../Button';
  import {
    closeButtonClass,
    closeButtonVariantClass,
    containerClass,
    containerVariantClass,
    contentClass,
    descriptionClass,
    detailRowClass,
    detailTextClass,
    elevatedClass,
    fullWidthClass,
    iconCircleClass,
    iconCircleVariantClass,
    messageClass,
    sectionClass,
    textBoxClass,
  } from './Toast.css';
  import type { ToastProps, ToastVariantType } from './Toast.types';
  import { ToastVariant } from './Toast.types';

  const toastIconMap: Record<ToastVariantType, TUNDR_ICONS> = {
    [ToastVariant.INFO]: TUNDR_ICONS.INFO,
    [ToastVariant.SUCCESS]: TUNDR_ICONS.CHECK_CIRCLE,
    [ToastVariant.WARNING]: TUNDR_ICONS.EXCLAMATION_TRIANGLE,
    [ToastVariant.ERROR]: TUNDR_ICONS.ERROR,
  };

  const props = withDefaults(defineProps<ToastProps>(), {
    variant: ToastVariant.INFO,
    copyable: true,
    closable: true,
    elevated: true,
    fullWidth: false,
  });

  const emit = defineEmits<{
    close: [];
    copy: [value: string];
  }>();

  const { t } = useLocale('ds-components');
  const slots = useSlots();
  const uid = useId();
  const { copy } = useClipboard();

  const resolvedTestId = computed(() => props.testId ?? `ds-toast-${uid}`);

  const containerClasses = computed(() => [
    containerClass,
    containerVariantClass[props.variant],
    {
      [elevatedClass]: props.elevated,
      [fullWidthClass]: props.fullWidth,
    },
  ]);

  const iconClasses = computed(() => [
    iconCircleClass,
    iconCircleVariantClass[props.variant],
  ]);

  const closeClasses = computed(() => [
    closeButtonClass,
    closeButtonVariantClass[props.variant],
  ]);

  const iconName = computed(() => props.icon ?? toastIconMap[props.variant]);

  const hasDetail = computed(() => !!slots.detail || !!props.detail);

  const isError = computed(() => props.variant === ToastVariant.ERROR);

  const ariaRole = computed(() => (isError.value ? 'alert' : 'status'));

  const ariaLive = computed(() => (isError.value ? 'assertive' : 'polite'));

  const onCopy = () => {
    const value = props.detail ?? '';
    copy(value);
    emit('copy', value);
  };
</script>

<template>
  <div
    :class="containerClasses"
    :id="id"
    :role="ariaRole"
    :aria-live="ariaLive"
    :data-distinct-id="distinctId"
    :data-testid="resolvedTestId"
  >
    <div :class="sectionClass">
      <slot name="icon">
        <div
          :class="iconClasses"
          aria-hidden="true"
          :data-testid="`${resolvedTestId}-icon`"
        >
          <FontIcon :icon-name="iconName" />
        </div>
      </slot>

      <div :class="contentClass">
        <div :class="textBoxClass">
          <div :class="messageClass" :data-testid="`${resolvedTestId}-message`">
            <slot name="message">{{ message }}</slot>
          </div>
          <div
            v-if="slots.description || description"
            :class="descriptionClass"
            :data-testid="`${resolvedTestId}-description`"
          >
            <slot name="description">{{ description }}</slot>
          </div>
        </div>

        <div v-if="hasDetail" :class="detailRowClass">
          <span
            :class="detailTextClass"
            :data-testid="`${resolvedTestId}-detail`"
          >
            <slot name="detail">{{ detail }}</slot>
          </span>
          <Button
            v-if="copyable && detail"
            is-ghost
            :variant="ButtonVariant.Icon"
            :size="ButtonSize.SM"
            :icon-name="TUNDR_ICONS.COPY"
            :aria-label="t('toast.copy_aria_label')"
            :test-id="`${resolvedTestId}-copy`"
            @click.stop="onCopy"
          />
        </div>
      </div>
    </div>

    <button
      v-if="closable"
      type="button"
      :class="closeClasses"
      :aria-label="t('toast.close_aria_label')"
      :data-testid="`${resolvedTestId}-close`"
      @click="emit('close')"
    >
      <FontIcon :icon-name="TUNDR_ICONS.CLOSE" />
    </button>
  </div>
</template>
